import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, DIMENSIONS, OUTCOMES, RAYG, RAYG_TEXT } from "../lib/api";
import { PageHeader } from "../components/Layout";

const DIM_LINKS = { strategy: "/organisation", process: "/workflows", people: "/work-roles", data: "/data", technology: "/technology", governance: "/governance" };

function HubVisual() {
  const cx = 340, cy = 260, r = 185;
  return (
    <svg viewBox="0 0 680 520" className="w-full max-w-3xl mx-auto" data-testid="enterprise-hub-visual">
      {DIMENSIONS.map((d, i) => {
        const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
        const x = cx + r * Math.cos(a), y = cy + r * Math.sin(a);
        return <line key={d.key} x1={cx} y1={cy} x2={x} y2={y} stroke={d.color} strokeWidth="1.5" strokeDasharray="4 4" opacity="0.5" />;
      })}
      <circle cx={cx} cy={cy} r="86" fill="#0A0A0A" />
      <circle cx={cx} cy={cy} r="86" fill="none" stroke="#D4AF37" strokeWidth="2" />
      <text x={cx} y={cy - 8} textAnchor="middle" fill="#D4AF37" fontSize="15" fontWeight="700" fontFamily="Cabinet Grotesk">Enterprise</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fill="#D4AF37" fontSize="15" fontWeight="700" fontFamily="Cabinet Grotesk">Outcomes</text>
      {DIMENSIONS.map((d, i) => {
        const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
        const x = cx + r * Math.cos(a), y = cy + r * Math.sin(a);
        return (
          <Link to={DIM_LINKS[d.key]} key={d.key}>
            <g className="cursor-pointer" data-testid={`hub-dimension-${d.key}`}>
              <circle cx={x} cy={y} r="52" fill="#FFFFFF" stroke={d.color} strokeWidth="2.5" />
              <text x={x} y={y + 4} textAnchor="middle" fill="#0A0A0A" fontSize="14" fontWeight="700" fontFamily="Cabinet Grotesk">{d.label}</text>
            </g>
          </Link>
        );
      })}
    </svg>
  );
}

export default function Home() {
  const [summary, setSummary] = useState(null);
  const [org, setOrg] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data));
    api.get("/organization").then((r) => setOrg(r.data)).catch(() => {});
  }, []);

  const kpis = summary ? [
    { label: "Operating-model integration score", value: summary.integration_score, link: "/diagnostics" },
    { label: "Average workflow health", value: summary.avg_workflow_health, link: "/workflows" },
    { label: "AI readiness", value: summary.ai_readiness, link: "/diagnostics" },
    { label: "Average diagnostic score", value: summary.avg_diagnostic, link: "/diagnostics" },
  ] : [];

  return (
    <div className="canvas-grain min-h-screen pb-16" data-testid="home-page">
      <PageHeader overline={org?.name || "Workspace"} title="One Enterprise System"
        subtitle="Enterprise Outcomes at the centre — six inseparable operating-model dimensions connected around them. Click any dimension to drill down." />
      <div className="px-8 grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-8">
        <div className="bg-white border border-black/10 rounded-sm p-6">
          <HubVisual />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
            {DIMENSIONS.map((d) => (
              <Link key={d.key} to={DIM_LINKS[d.key]} className="chip border border-black/10 rounded-sm p-3 bg-[#FDFBF7]" data-testid={`dimension-card-${d.key}`}>
                <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: d.color }} />
                <span className="font-display font-bold text-sm">{d.label}</span>
                <p className="text-[11px] text-black/50 mt-1 leading-snug">{d.desc}</p>
              </Link>
            ))}
          </div>
        </div>
        <div className="space-y-4">
          {kpis.map((k) => (
            <Link to={k.link} key={k.label} className="chip block bg-[#0A0A0A] text-white rounded-sm p-5" data-testid="home-kpi-card">
              <div className="flex items-end justify-between">
                <p className="text-[11px] uppercase tracking-[0.15em] text-white/60 max-w-[160px]">{k.label}</p>
                <span className="font-display text-4xl font-black" style={{ color: RAYG(k.value) }}>{k.value}</span>
              </div>
              <div className="h-1 mt-3 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${k.value}%`, background: RAYG(k.value) }} />
              </div>
            </Link>
          ))}
          {summary && (
            <div className="bg-white border border-black/10 rounded-sm p-5">
              <p className="text-[11px] uppercase tracking-[0.15em] text-black/50 mb-3">Top AI opportunities</p>
              {summary.high_ai_opportunities.map((o) => (
                <Link to="/capabilities" key={o.name} className="flex items-center justify-between py-1.5 border-b border-black/5 last:border-0 group" data-testid="home-ai-opportunity">
                  <span className="text-[13px] group-hover:text-[#D4AF37] transition-colors">{o.name}</span>
                  <span className="text-[11px] font-mono px-1.5 py-0.5 rounded-sm" style={{ background: RAYG(o.score), color: RAYG_TEXT(o.score) }}>{o.score}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
      {org && (
        <div className="px-8 mt-8">
          <div className="bg-white border border-black/10 rounded-sm p-6">
            <p className="text-[11px] uppercase tracking-[0.15em] text-black/50 mb-3">Strategic outcomes this workspace serves</p>
            <div className="flex flex-wrap gap-2">
              {OUTCOMES.map((o) => (
                <span key={o} className="text-[12px] border border-[#D4AF37]/50 text-black/70 rounded-full px-3 py-1">{o}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
